import { nsCustomization } from '@/app/i18n/namespaces';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import { Input } from '@/common-utils/components/ui/input';
import Select from '@/common-utils/components/ui/select';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/common-utils/components/ui/tooltip';
import ICONS from '@/common-utils/enums/icons';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import PersoPDFFile from '../pdf/PersoPDFFile';
import LANGUAGE_OPTIONS from '../../enums/LanguageOptions';
import formatPackageCode from '@/common-utils/functions/formatPackageCode';
import CUSTOMIZATION_URL_ACTIONS from '../../enums/CustomizationUrlActions';
import { useEffect } from 'react';
/**
 * CustomizationIdSection - Displays the identification section of a customization
 * Includes: identifier, name, locale, and QR code (when published)
 *
 * @param {object} props - Component props
 * @param {object} props.customization - The customization object
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.action - Current page action (show, duplicate, etc.)
 * @param {boolean} props.isPackage - Whether the customization is a published package
 * @param {string} props.packageLabel - The package label/name
 * @param {string} props.locale - The selected locale
 * @param {Function} props.onLabelChange - Callback when label changes
 * @param {Function} props.onLocaleChange - Callback when locale changes
 * @param {string} props.merchantAccountLabel - The organization name for PDF generation
 * @param {Function} props.setIsDirtyCustomization - Callback when isDirtyCustomization changes
 * @param {Function} props.setIsValidCustomization - Callback when isValidCustomization changes
 * @returns {JSX.Element} CustomizationIdSection component
 */
function CustomizationIdSection({
    customization,
    isLoading = false,
    action = '',
    isPackage = false,
    packageLabel = '',
    locale = 'fr-FR',
    onLabelChange,
    onLocaleChange,
    merchantAccountLabel = '',
    setIsDirtyCustomization,
    setIsValidCustomization
}) {
    // Clear the package code when duplicating so the new copy starts empty
    const normalizedPackageCode = action === CUSTOMIZATION_URL_ACTIONS.DUPLICATE ? '' : customization?.packageCode;

    // Format the package code for display
    const formattedPackageCode = formatPackageCode(normalizedPackageCode);

    // Check if inputs should be disabled
    const isDisabled = action === CUSTOMIZATION_URL_ACTIONS.SHOW || (isPackage && action !== CUSTOMIZATION_URL_ACTIONS.DUPLICATE);

    useEffect(() => {
        // Looks for package label validity
        if (packageLabel !== '') {
            setIsValidCustomization(true);
        } else {
            setIsValidCustomization(false);
        }

        // Looks for package label and locale changes
        if (packageLabel !== customization?.packageLabel
            || locale !== customization?.locale) {
            setIsDirtyCustomization(true);
        } else {
            setIsDirtyCustomization(false);
        }
    }, [customization, packageLabel, locale]);

    // Loading skeleton
    if (isLoading) {
        return (
            <div className='flex flex-col lg:flex-row gap-8 py-6 px-4 lg:px-8'>
                <div className='flex flex-col gap-5 lg:flex-1'>
                    {[1, 2, 3].map((i) => (
                        <div key={i} className='flex flex-col md:flex-row md:items-center gap-2 md:gap-6'>
                            <Skeleton className='h-4 w-40 bg-gray-200' />
                            <Skeleton className='h-10 w-full md:w-72 bg-gray-200' />
                        </div>
                    ))}
                </div>
                <div className='flex items-center justify-center lg:flex-1'>
                    <Skeleton className='h-40 w-40 bg-gray-200' />
                </div>
            </div>
        );
    }

    return (
        <div className='flex flex-col lg:flex-row gap-8 py-2 px-4 lg:px-8'>
            {/* Column 1: Form fields section */}
            <div className='flex flex-col gap-5 lg:flex-1'>
                {/* Code initialisation field */}
                <div className='flex flex-col md:flex-row md:items-center gap-2 md:gap-6'>
                    <span className='text-gray-600 md:w-44 shrink-0 text-sm'>
                        <Entity entity='details.fields.init_code.label' namespace={nsCustomization} />
                    </span>
                    <div className='relative w-full md:w-72'>
                        <Input
                            disabled
                            type='text'
                            name='identifier'
                            value={formattedPackageCode}
                            className='w-full bg-gray-100 border-gray-200 pr-8'
                        />
                        {!isPackage && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className='hidden md:inline-flex absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer'>
                                            <Icon icon={ICONS.HELP} className='text-gray-400 hover:text-gray-600' />
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side='top'>
                                        <Entity entity='details.fields.init_code.tooltip' namespace={nsCustomization} />
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                </div>

                {/* Nom personnalisation field */}
                <div className='flex flex-col md:flex-row md:items-center gap-2 md:gap-6'>
                    <span className='text-gray-600 md:w-44 shrink-0 text-sm'>
                        <Entity entity='details.fields.name.label' namespace={nsCustomization} />
                        <span className='text-danger ml-0.5'>*</span>
                    </span>
                    <Input
                        disabled={isDisabled}
                        type='text'
                        maxLength={100}
                        name='packageLabel'
                        value={packageLabel}
                        onChange={(e) => onLabelChange?.(e.target.value)}
                        autoComplete='off'
                        className={`w-full md:w-72 ${!packageLabel && !isDisabled ? 'border-danger' : 'border-gray-200'}`}
                    />
                </div>

                {/* Choix langue field */}
                <div className='flex flex-col md:flex-row md:items-center gap-2 md:gap-6'>
                    <span className='text-gray-600 md:w-44 shrink-0 text-sm'>
                        <Entity entity='details.fields.locale.label' namespace={nsCustomization} />
                    </span>
                    <Select
                        disabled={isDisabled}
                        options={LANGUAGE_OPTIONS}
                        value={locale}
                        onChange={(value) => onLocaleChange(LANGUAGE_OPTIONS.find((lang) => lang.value === value))}
                        className='w-full md:w-72'
                    />
                </div>
            </div>

            {/* Column 2: QR Code section - only visible when package is published and in show mode */}
            {isPackage && action === CUSTOMIZATION_URL_ACTIONS.SHOW && normalizedPackageCode && (
                <div className='flex flex-col items-center justify-center gap-3 lg:flex-1'>
                    <QRCodeSVG
                        id='customization-qrcode-svg'
                        value={normalizedPackageCode}
                        size={120}
                        level='M'
                        includeMargin={false}
                    />
                    <p className='text-sm text-gray-500 text-center max-w-[280px] 2xl:max-w-none 2xl:whitespace-nowrap'>
                        <Entity entity='modal.qr_code.description' namespace={nsCustomization} />
                    </p>
                    <PersoPDFFile
                        customization={customization}
                        merchantAccountLabel={merchantAccountLabel}
                        qrCodeElementId='customization-qrcode-svg'
                    />
                </div>
            )}
        </div>
    );
}

CustomizationIdSection.propTypes = {
    customization: PropTypes.shape({
        packageCode: PropTypes.string,
        packageLabel: PropTypes.string,
        locale: PropTypes.string
    }),
    isLoading: PropTypes.bool,
    action: PropTypes.string,
    isPackage: PropTypes.bool,
    packageLabel: PropTypes.string,
    locale: PropTypes.string,
    onLabelChange: PropTypes.func,
    onLocaleChange: PropTypes.func,
    merchantAccountLabel: PropTypes.string,
    setIsDirtyCustomization: PropTypes.func,
    setIsValidCustomization: PropTypes.func
};

export default CustomizationIdSection;

